import PropTypes from 'prop-types';
import { BsCheckCircle, BsExclamationTriangle, BsXCircle, BsFiles, BsInfoCircle } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';

const CSV_ERROR_MESSAGES = {
	MISSING_COMPANY_NAME: 'Firmenname fehlt',
	MISSING_WEBSITE_URL: 'Website / Domain fehlt',
	INVALID_WEBSITE_URL: 'Ungültige Website-URL',
	PROCESSING_ERROR: 'Fehler bei der Verarbeitung',
};

function translateReason(reason) {
	return CSV_ERROR_MESSAGES[reason] || reason;
}

/**
 * Group errors by reason and collect row numbers per group.
 */
function groupErrors(errors) {
	const groups = {};
	for (const error of errors) {
		const reason = error.reason;
		if (!groups[reason]) {
			groups[reason] = [];
		}
		groups[reason].push(error.row);
	}
	return Object.entries(groups).map(([reason, rows]) => ({
		reason,
		label: translateReason(reason),
		count: rows.length,
		rows,
	}));
}

export default function ImportConfirmStep({
	fileName,
	totalRows,
	columnMapping,
	isLoading,
	importResult,
	onImport,
	onBack,
	onClose,
}) {
	// Get mapped field labels
	const getMappedFields = () => {
		const fieldLabels = {
			companyName: 'Firmenname',
			websiteUrl: 'Website',
			addressStreet: 'Straße',
			addressPostal: 'PLZ',
			addressCity: 'Stadt',
		};

		return Object.entries(columnMapping)
			.filter(([, csvCol]) => csvCol)
			.map(([field, csvCol]) => `${fieldLabels[field]} (${csvCol})`);
	};

	// If we have a result, show the result view
	if (importResult) {
		const errorCount = importResult.errors?.length || 0;
		const duplicateCount = importResult.duplicates || 0;
		const isSuccess = importResult.success && importResult.imported > 0;
		const errorGroups = importResult.errors ? groupErrors(importResult.errors) : [];

		return (
			<div className="space-y-5">
				{/* Success/Error Header */}
				<div className="text-center">
					{isSuccess ? (
						<BsCheckCircle className="mx-auto h-12 w-12 text-success-500" />
					) : (
						<BsXCircle className="mx-auto h-12 w-12 text-warning-500" />
					)}
					<h3 className="mt-3 text-lg font-medium text-text">
						{isSuccess ? 'Import abgeschlossen' : 'Import fehlgeschlagen'}
					</h3>
				</div>

				{/* Statistics */}
				<div className="bg-gray-50 rounded-lg p-4">
					<div
						className={`grid gap-4 text-center ${duplicateCount > 0 && errorCount > 0 ? 'grid-cols-3' : duplicateCount > 0 || errorCount > 0 ? 'grid-cols-2' : 'grid-cols-1'}`}
					>
						{/* Imported */}
						<div>
							<p className="text-lg font-bold text-success-700">{importResult.imported}</p>
							<p className="text-sm font-medium text-text">Importiert</p>
							<p className="text-xs text-bgBase-800 mt-0.5">Neue Firmen angelegt</p>
						</div>

						{/* Duplicates */}
						{duplicateCount > 0 && (
							<div>
								<p className="text-lg font-bold text-bgBase-800">{duplicateCount}</p>
								<p className="text-sm font-medium text-text">Duplikate</p>
								<p className="text-xs text-bgBase-800 mt-0.5">Gleiche Domain mehrfach in CSV</p>
							</div>
						)}

						{/* Errors */}
						{errorCount > 0 && (
							<div>
								<p className="text-lg font-bold text-warning-700">{errorCount}</p>
								<p className="text-sm font-medium text-text">Fehlerhaft</p>
								<p className="text-xs text-bgBase-800 mt-0.5">Zeilen mit fehlenden Daten</p>
							</div>
						)}
					</div>
				</div>

				{/* Duplicate Info */}
				{duplicateCount > 0 && (
					<div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
						<div className="flex items-start gap-2">
							<BsFiles className="h-4 w-4 text-bgBase-800 mt-0.5 flex-shrink-0" />
							<p className="text-sm text-text">
								<strong>{duplicateCount} Zeilen</strong> wurden übersprungen, weil die gleiche
								Website-Domain bereits in einer anderen Zeile vorkommt. Pro Domain wird nur die erste
								Zeile importiert.
							</p>
						</div>
					</div>
				)}

				{/* Error Details — grouped by reason */}
				{errorCount > 0 && (
					<div className="bg-red-50 border border-red-200 rounded-lg p-4">
						<div className="flex items-start gap-2">
							<BsExclamationTriangle className="h-4 w-4 text-warning-500 mt-0.5 flex-shrink-0" />
							<div className="w-full">
								<h4 className="text-sm font-medium text-warning-700">
									{errorCount} Zeilen konnten nicht importiert werden
								</h4>
								<div className="mt-3 space-y-2">
									{errorGroups.map((group) => (
										<div key={group.reason} className="bg-white/60 rounded p-2.5">
											<div className="flex items-center justify-between">
												<span className="text-sm font-medium text-warning-700">
													{group.label}
												</span>
												<span className="text-xs font-medium text-warning-500 bg-red-100 rounded-full px-2 py-0.5">
													{group.count}x
												</span>
											</div>
											<p className="text-xs text-bgBase-800 mt-1">
												Zeilen:{' '}
												{group.rows.length <= 20
													? group.rows.join(', ')
													: `${group.rows.slice(0, 20).join(', ')} … und ${group.rows.length - 20} weitere`}
											</p>
										</div>
									))}
								</div>
							</div>
						</div>
					</div>
				)}

				{/* Close Button */}
				<div className="flex justify-end">
					<Button variant="solid" onClick={onClose}>
						Schließen
					</Button>
				</div>
			</div>
		);
	}

	// Confirmation view (before import)
	return (
		<div className="space-y-6">
			<div>
				<h3 className="text-lg font-medium text-text mb-1">Import bestätigen</h3>
				<p className="text-sm text-bgBase-800">Überprüfen Sie die Einstellungen vor dem Import</p>
			</div>

			{/* Summary */}
			<div className="bg-gray-50 rounded-lg p-4 space-y-3">
				<div className="flex justify-between">
					<span className="text-sm text-bgBase-800">Datei:</span>
					<span className="text-sm font-medium text-text">{fileName}</span>
				</div>
				<div className="flex justify-between">
					<span className="text-sm text-bgBase-800">Anzahl Zeilen:</span>
					<span className="text-sm font-medium text-text">{totalRows}</span>
				</div>
				<div className="flex justify-between items-start">
					<span className="text-sm text-bgBase-800">Zugeordnete Felder:</span>
					<div className="text-right">
						{getMappedFields().map((field, index) => (
							<span
								key={index}
								className="inline-block text-xs bg-purple-100 text-primary-700 rounded px-2 py-0.5 ml-1 mb-1"
							>
								{field}
							</span>
						))}
					</div>
				</div>
			</div>

			{/* Info Text */}
			<div className="bg-primary-100 border border-primary-200 rounded-lg p-3">
				<div className="flex items-start gap-2">
					<BsInfoCircle className="h-4 w-4 text-primary-500 mt-0.5 flex-shrink-0" />
					<p className="text-sm text-primary-700">
						Es wird eine neue Suchanfrage mit dem Namen <strong>&ldquo;{fileName}&rdquo;</strong> erstellt.
						Die Firmen können anschließend angereichert werden.
					</p>
				</div>
			</div>

			{/* Navigation Buttons */}
			<div className="flex justify-between">
				<Button variant="outline" onClick={onBack} disabled={isLoading}>
					Zurück
				</Button>
				<Button variant="solid" onClick={onImport} disabled={isLoading}>
					{isLoading ? (
						<>
							<span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
							Importiere...
						</>
					) : (
						`${totalRows} Firmen importieren`
					)}
				</Button>
			</div>
		</div>
	);
}

ImportConfirmStep.propTypes = {
	fileName: PropTypes.string.isRequired,
	totalRows: PropTypes.number.isRequired,
	columnMapping: PropTypes.object.isRequired,
	isLoading: PropTypes.bool,
	importResult: PropTypes.object,
	onImport: PropTypes.func.isRequired,
	onBack: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired,
};
